import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiCalendar, FiMapPin, FiUsers, FiClock } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import CreateEventModal from '../components/CreateEventModal';

const Container = styled.div`
  padding: ${props => props.theme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxl};
  font-weight: 700;
  color: ${props => props.theme.colors.gray[800]};
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.gray[800]};
    transform: translateY(-1px);
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xl};
`;

const EventCard = styled.div`
  background: white;
  border-radius: ${props => props.theme.borders.radius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const EventImage = styled.div.withConfig({
  shouldForwardProp: (prop) => !['image'].includes(prop)
})`
  height: 200px;
  background: ${props => props.image ? `url(${props.image})` : props.theme.colors.gray[200]};
  background-size: cover;
  background-position: center;
  position: relative;

  ${props => !props.image && `
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props.theme.colors.gray[500]};
    font-size: ${props.theme.fontSizes.sm};
  `}
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => !['status'].includes(prop)
})`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background: ${props => {
    switch (props.status) {
      case 'active': return props.theme.colors.success;
      case 'inactive': return props.theme.colors.warning;
      case 'cancelled': return props.theme.colors.danger;
      case 'completed': return props.theme.colors.gray[500];
      default: return props.theme.colors.gray[500];
    }
  }};
  color: white;
  border-radius: ${props => props.theme.borders.radius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
`;

const EventContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const EventTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: 600;
  color: ${props => props.theme.colors.gray[800]};
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.4;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[600]};
  font-size: ${props => props.theme.fontSizes.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const EventDescription = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  font-size: ${props => props.theme.fontSizes.sm};
  line-height: 1.5;
  margin-bottom: ${props => props.theme.spacing.lg};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EventActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
`;

const ActionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant'].includes(prop)
})`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.colors.gray[600]};
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.sm};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.colors.gray[800] : props.theme.colors.gray[50]};
    transform: translateY(-1px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${props => props.theme.colors.gray[600]};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.gray[600]};

  h3 {
    font-size: ${props => props.theme.fontSizes.lg};
    margin-bottom: ${props => props.theme.spacing.md};
  }

  p {
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const TicketTypesInfo = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
`;

const ManageUpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const { token } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/events?status=active&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleCreateEvent = () => {
    setEditingEvent(null); // Clear any editing event
    setShowCreateModal(true);
  };

  const handleEventSuccess = () => {
    fetchEvents(); // Refresh the events list
    setEditingEvent(null); // Clear editing state
  };

  const handleViewEvent = (eventId) => {
    // TODO: Implement view event details
    console.log('View event:', eventId);
  };

  const handleEditEvent = async (eventId) => {
    console.log('Edit event:', eventId);
    
    try {
      // Fetch fresh event data from API to get latest tax settings
      console.log('🔄 Fetching fresh event data for editing...');
      const response = await fetch(`${API_URL}/events/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const eventToEdit = data.event;
        console.log('✅ Fresh event data loaded:', {
          name: eventToEdit.name,
          taxEnabled: eventToEdit.taxEnabled,
          taxRate: eventToEdit.taxRate
        });
        setEditingEvent(eventToEdit);
        setShowCreateModal(true);
      } else {
        console.error('❌ Failed to fetch fresh event data, using cached data');
        const eventToEdit = events.find(event => event._id === eventId);
        if (eventToEdit) {
          setEditingEvent(eventToEdit);
          setShowCreateModal(true);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching fresh event data:', error);
      // Fallback to cached data
      const eventToEdit = events.find(event => event._id === eventId);
      if (eventToEdit) {
        setEditingEvent(eventToEdit);
        setShowCreateModal(true);
      }
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Check if it's a ticket-related error
        if (errorData.cannotDelete && errorData.ticketsCount) {
          alert(`Cannot delete event: ${errorData.ticketsCount} tickets are already booked for this event. Please refund all tickets before deleting.`);
          return;
        }
        
        // Check if it's a payment-related error
        if (errorData.cannotDelete && errorData.paymentsCount) {
          alert(`Cannot delete event: ${errorData.paymentsCount} active payments exist for this event. Please process refunds first.`);
          return;
        }
        
        throw new Error(errorData.message || 'Failed to delete event');
      }

      const result = await response.json();
      alert(result.message || 'Event cancelled successfully');
      
      // Refresh events list
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      alert(err.message || 'Failed to delete event');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Manage Upcoming Events</Title>
        </Header>
        <LoadingContainer>
          <div>Loading events...</div>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>Manage Upcoming Events</Title>
        </Header>
        <div style={{ color: 'red', textAlign: 'center', padding: '2rem' }}>
          Error: {error}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Manage Upcoming Events</Title>
        <CreateButton onClick={handleCreateEvent}>
          <FiPlus />
          Create New Event
        </CreateButton>
      </Header>

      {events.length === 0 ? (
        <EmptyState>
          <h3>No Events Found</h3>
          <p>You haven't created any events yet.</p>
          <CreateButton onClick={handleCreateEvent}>
            <FiPlus />
            Create Your First Event
          </CreateButton>
        </EmptyState>
      ) : (
        <EventsGrid>
          {events.map((event) => (
            <EventCard key={event._id}>
              <EventImage image={event.image}>
                {!event.image && 'No Image'}
                <StatusBadge status={event.status}>{event.status}</StatusBadge>
              </EventImage>
              
              <EventContent>
                <EventTitle>{event.name}</EventTitle>
                
                <EventDetail>
                  <FiCalendar />
                  <span>{formatDate(event.date)} at {formatTime(event.time)}</span>
                </EventDetail>
                
                <EventDetail>
                  <FiMapPin />
                  <span>{event.venue}, {event.location}</span>
                </EventDetail>
                
                {event.maxCapacity > 0 && (
                  <EventDetail>
                    <FiUsers />
                    <span>Max Capacity: {event.maxCapacity}</span>
                  </EventDetail>
                )}

                <EventDescription>{event.description}</EventDescription>

                {event.ticketTypes && event.ticketTypes.length > 0 && (
                  <TicketTypesInfo>
                    <strong>{event.ticketTypes.length} Ticket Type{event.ticketTypes.length !== 1 ? 's' : ''}</strong>
                    {event.ticketTypes.map((ticket, index) => (
                      <div key={ticket._id} style={{ marginTop: '4px' }}>
                        • {ticket.typeName}: {ticket.price} {ticket.currency} ({ticket.availableQuantity} available)
                        {ticket.expiryDate && (
                          <span style={{ color: '#ef4444', marginLeft: '8px' }}>
                            <FiClock style={{ display: 'inline', marginRight: '2px' }} />
                            Expires: {formatDate(ticket.expiryDate)}
                          </span>
                        )}
                      </div>
                    ))}
                  </TicketTypesInfo>
                )}

                <EventActions>
                  <ActionButton variant="primary" onClick={() => handleViewEvent(event._id)}>
                    <FiEye />
                    View
                  </ActionButton>
                  <ActionButton onClick={() => handleEditEvent(event._id)}>
                    <FiEdit2 />
                    Edit
                  </ActionButton>
                  <ActionButton onClick={() => handleDeleteEvent(event._id)}>
                    <FiTrash2 />
                    Delete
                  </ActionButton>
                </EventActions>
              </EventContent>
            </EventCard>
          ))}
        </EventsGrid>
      )}
      
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingEvent(null);
        }}
        onSuccess={handleEventSuccess}
        eventToEdit={editingEvent}
      />
    </Container>
  );
};

export default ManageUpcomingEvents;
